import {
  AppointmentRequest,
  AppointmentResponse,
  ILabCase,
} from '@dataTypes/appointment.type'
import axios from './axiosConfig'

import { getEnv } from '@utils/env'
import { logger } from '@utils/safeLogger'
import { toApiError } from './errors/api-error'

// Constants
const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_APPOINTMENT', '')

const headers = {
  'Content-Type': 'application/json',
}

// Types
interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

type AppointmentsResponseData = {
  appointments?: AppointmentResponse[]
}

export interface AppointmentSummary {
  appointment_id: string
  patient_id: string
  operatory_id?: string | null
  appt_date: string
  appt_start: string
  appointment_start_date?: string
  patient?: {
    patient_id: string
    first_name?: string
    last_name?: string
    cellphone?: string
    public_id?: string | null
  } | null
  appointmenttype?: {
    name: string
  } | null
}

type AppointmentSummaryResponseData = {
  appointments?: AppointmentSummary[]
}

type AvailableSlotsResponseData = {
  operatory_id: string
  slots: {
    start_time: string
    end_time: string
    procedure_time: string
  }[]
}

type LabCasesResponseData = {
  lab_cases: ILabCase[]
}

type GenericResponseData = Record<string, unknown>

export type GetAllApptResponse = ApiResponse<AppointmentsResponseData>
export type GetAppointmentSummaryResponse =
  ApiResponse<AppointmentSummaryResponseData>
export type UpdateApptResponse = ApiResponse<AppointmentsResponseData>
export type CreateApptResponse = ApiResponse<GenericResponseData>
export type AvailableSlotsResponse = ApiResponse<AvailableSlotsResponseData>
export type LabCasesResponse = ApiResponse<LabCasesResponseData>
export type IsSlotAvailableResponse = ApiResponse<any>
export type GetAllApptProceduresByPatientIdResponse = ApiResponse<any>

const getAppointmentReferenceValues = (): any => {
  return {
    user_id: JSON.parse(localStorage.getItem('user') || '{}')?.user_id || '',
    clinic_id: localStorage.getItem('clinic_id') || '',
    db_name: localStorage.getItem('db_name') || '',
  }
}

// API Functions
export const getAllAppointments = async (
  startDate: string | null,
  endDate: string | null,
): Promise<GetAllApptResponse> => {
  try {
    const response = await axios.get<GetAllApptResponse>(
      `${BUSINESS_LAYER_API}/api/v1/appointments`,
      {
        params: {
          start_date: startDate,
          end_date: endDate,
        },
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in getAllAppointments:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getAppointmentsSummary = async (
  startDate: string,
  endDate: string,
): Promise<GetAppointmentSummaryResponse> => {
  try {
    const response = await axios.get<GetAppointmentSummaryResponse>(
      `${BUSINESS_LAYER_API}/api/v1/appointments/summary`,
      {
        params: {
          start_date: startDate,
          end_date: endDate,
        },
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in getAppointmentsSummary:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updateAppointmentById = async (
  appointment_id: string,
  updatedData: Partial<AppointmentRequest>,
  updateByDragAndDrop: boolean = false,
  isCancelledAppointment: boolean = false,
): Promise<UpdateApptResponse> => {
  try {
    const response = await axios.put<UpdateApptResponse>(
      `${BUSINESS_LAYER_API}/api/v1/appointments`,
      { appointment_id, data: updatedData },
      {
        headers,
        params: {
          ...(updateByDragAndDrop && { update_by_drag_and_drop: true }),
          ...(isCancelledAppointment && { is_cancelled_appointment: true }),
        },
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in updateAppointmentById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const createAppointment = async (
  apptDetails: Partial<AppointmentRequest>,
): Promise<CreateApptResponse> => {
  try {
    const response = await axios.post<CreateApptResponse>(
      `${BUSINESS_LAYER_API}/api/v1/appointments`,
      apptDetails,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in createAppointment:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const deleteAppointmentById = async (
  apptId: string,
): Promise<CreateApptResponse> => {
  try {
    const response = await axios.delete<CreateApptResponse>(
      `${BUSINESS_LAYER_API}/api/v1/appointments/${apptId}`,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in deleteAppointmentById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getAllAvailableSlots = async (data: {
  provider_id: string
  patient_id?: string | null
  operatory_id: string
  date: string
  duration: number
  exclude_appointment_id?: string
}): Promise<AvailableSlotsResponse> => {
  try {
    const { patient_id, ...slotRequest } = data
    const response = await axios.post<AvailableSlotsResponse>(
      `${BUSINESS_LAYER_API}/api/v1/appointments/slot`,
      {
        ...slotRequest,
        ...(patient_id ? { patient_id } : {}),
      },
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in deleteAppointmentById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getAllLabCases = async (
  patientId?: string,
): Promise<LabCasesResponse> => {
  try {
    const baseUrl = `${BUSINESS_LAYER_API}/api/v1/labcases`
    const url = patientId ? `${baseUrl}?patient_id=${patientId}` : baseUrl
    const response = await axios.get<LabCasesResponse>(url, {
      headers,
    })

    return response.data
  } catch (e: unknown) {
    logger.error('Error in getAllLabCases:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updateAppointmentStatusById = async (
  appointment_id: string,
  status: number,
): Promise<UpdateApptResponse> => {
  try {
    const response = await axios.put<UpdateApptResponse>(
      `${BUSINESS_LAYER_API}/api/v1/appointments/${appointment_id}/status/${status}`,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in updateAppointmentById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getAppointmentById = async (
  appointmentId: string,
): Promise<ApiResponse<{ appointment: AppointmentResponse | null }>> => {
  try {
    const response = await axios.get<
      ApiResponse<{ appointment: AppointmentResponse | null }>
    >(`${BUSINESS_LAYER_API}/api/v1/appointments/${appointmentId}`, {
      headers,
    })

    return response.data
  } catch (e: unknown) {
    logger.error('Error in getAppointmentById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getAppointmentsByPatientId = async (
  patientId: string,
  startDate?: string,
  endDate?: string,
  cancelledOnly?: boolean,
): Promise<GetAllApptResponse> => {
  try {
    const response = await axios.get<GetAllApptResponse>(
      `${BUSINESS_LAYER_API}/api/v1/appointments/patient/${patientId}`,
      {
        params: {
          ...(startDate && { start_date: startDate }),
          ...(endDate && { end_date: endDate }),
          ...(cancelledOnly && { cancelled_only: cancelledOnly }),
        },
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in getAppointmentsByPatientId:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getAppointmentsByProviderId = async (
  providerId: string,
  startDate?: string,
  endDate?: string,
): Promise<GetAllApptResponse> => {
  try {
    const response = await axios.get<GetAllApptResponse>(
      `${BUSINESS_LAYER_API}/api/v1/appointments/provider/${providerId}`,
      {
        params: {
          ...(startDate && { start_date: startDate }),
          ...(endDate && { end_date: endDate }),
        },
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in getAppointmentsByProviderId:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const isSlotAvailable = async (data: {
  provider_id: string
  operatory_id: string
  date: string
  start_time: string
  end_time: string
  duration: number
  exclude_appointment_id?: string
}): Promise<any> => {
  try {
    const response = await axios.post<any>(
      `${BUSINESS_LAYER_API}/api/v1/appointments/slot/is-available`,
      data,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in isSlotAvailable:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const availableProviders = async (data: {
  patient_id: string
  operatory_id: string
  provider_id?: string
  date: string
  start_time: string
  end_time: string
  duration: number
  exclude_appointment_id?: string
}): Promise<any> => {
  try {
    const response = await axios.post<any>(
      `${BUSINESS_LAYER_API}/api/v1/appointments/slot/available-providers`,
      data,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in availableProviders:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getAllApptProceduresByPatientId = async (
  patientId: string,
  includeAppointmentId?: string,
): Promise<GetAllApptProceduresByPatientIdResponse> => {
  try {
    const response = await axios.get<GetAllApptProceduresByPatientIdResponse>(
      `${BUSINESS_LAYER_API}/api/v1/apptprocedures/${patientId}`,
      {
        headers,
        params: {
          ...(includeAppointmentId
            ? { include_appointment_id: includeAppointmentId }
            : {}),
        },
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in GetInsurancePlansByPatientId:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const applySchedulerSettings = async (data: any): Promise<any> => {
  try {
    const { user_id, clinic_id } = getAppointmentReferenceValues()
    data.user_id = user_id
    data.clinic_id = clinic_id
    const response = await axios.post<any>(
      `${BUSINESS_LAYER_API}/api/v1/schedulersettings`,
      data,
      {
        headers,
      },
    )

    return response.data
  } catch (error: any) {
    logger.error('Error applying scheduler settings:', error)
    throw toApiError(error, 'Error applying scheduler settings')
  }
}

export const getSchedulerSettings = async (): Promise<any> => {
  try {
    const response = await axios.get<any>(
      `${BUSINESS_LAYER_API}/api/v1/schedulersettings`,
      {
        headers,
      },
    )

    return response.data
  } catch (error: any) {
    logger.error('Error getting scheduler settings:', error)
    throw toApiError(error, 'Error getting scheduler settings')
  }
}
